/**
 * useMatchmakingSocket.ts
 * Hook for managing the matchmaking WebSocket connection
 */

import { useCallback, useEffect, useState } from 'react';

import {
  ERematchRequestStatus,
  MATCHMAKING_WS_NAMESPACE,
  EFreePlayDifficulty,
  type IGameAction,
  type WsTicketQueryDto,
} from '@skillarea/lib-shared';

import { ensureDeviceId, getCachedDeviceId } from '~/lib/security/device-id';
import {
  MATCHMAKING_INSUFFICIENT_BALANCE_ERROR,
  MATCHMAKING_SEARCHING_FOR_OPPONENT_MESSAGE,
  MATCHMAKING_STARTING_FREE_PLAY_MESSAGE,
} from '~/lib/matchmakingLocale';
import { getSocketIOClient, type Socket } from '~/lib/socket-client';
import { wsTicketStore } from '~/stores/wsTickerStore';
import { MatchmakingService } from '~/services/matchmakingService';
import {
  extractMaintenanceMessageFromMatchmakingMessage,
  isMatchmakingMaintenanceMessage,
  maintenanceStore,
  showMatchmakingMaintenanceWarningToast,
} from '~/stores/maintenanceStore';
import {
  OVERLAP_DRAIN_MATCHMAKING_BLOCK_MESSAGE,
  overlapStore,
} from '~/stores/overlapStore';

// Prefer SERVER_API_TARGET in E2E/dev (injected by lib-tests server harness).
const BASE_URL = process.env.BASE_URL || process.env.SERVER_API_TARGET || '';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

type TRouteHint = NonNullable<WsTicketQueryDto['routeColor']> | null;

function parseRouteColor(value: unknown): TRouteHint {
  return value === 'blue' || value === 'green' ? value : null;
}

function extractResolvedRouteColor(response: unknown): TRouteHint {
  if (!isRecord(response)) {
    return null;
  }

  const directRouteColor = parseRouteColor(response.routeColor);

  if (directRouteColor !== null) {
    return directRouteColor;
  }

  return isRecord(response.data)
    ? parseRouteColor(response.data.routeColor)
    : null;
}

export type TMatchmakingState =
  | 'idle'
  | 'connecting'
  | 'stake_selection'
  | 'searching'
  | 'waiting_for_opponent'
  | 'stake_offer'
  | 'stake_offer_declined'
  | 'game_confirm'
  | 'preparing'
  | 'playing'
  | 'win'
  | 'lose'
  | 'draw'
  | 'active_game_exists'
  | 'cancelled'
  | 'error';

export type TGameResult = 'win' | 'lose' | 'draw';

interface IStakeOfferData {
  lobbyId: string;
  suggestedStake: number;
  reason: string;
  message?: string;
}

interface IGameStartedData {
  lobbyId: string;
  gameId: number;
  creatorId: number;
  opponentId: number;
  creatorName: string;
  opponentName: string;
  creatorAvatar?: string;
  opponentAvatar?: string;
  stake: number;
  /** Player slot (0 or 1), set by the game engine. */
  playerSlot?: 0 | 1;
  /** Remaining time for engine slot 0. */
  player1TimeLeftMs?: number;
  /** Remaining time for engine slot 1. */
  player2TimeLeftMs?: number;
  /** Server match start time (unix ms). */
  startAtMs?: number;
  /** Client readiness deadline (unix ms). */
  readyDeadlineAtMs?: number;
  /** Match confirmation deadline (unix ms). */
  confirmDeadlineAtMs?: number;
  /** Whether the current user has already confirmed the match. */
  currentUserConfirmed?: boolean;
  message: string;
}

interface IGameUpdateData {
  lobbyId: string;
  gameState: unknown;
  yourTurn: boolean;
  /** Player slot (0 or 1), set by the game engine. E.g. in chess 0 = white, 1 = black. */
  playerSlot?: 0 | 1;
  player1TimeLeftMs?: number;
  player2TimeLeftMs?: number;
  message?: string;
}

interface IGameFinishedData {
  lobbyId: string;
  isWin: boolean;
  isDraw?: boolean;
  prize: number;
  finalScore?: {
    creatorScore: number;
    opponentScore: number;
  };
  message: string;
  chestUnlockedDayIndex?: number;
}

interface ISearchCancelledData {
  message?: string;
}

export interface ISearchStateData {
  timeout?: number;
  message?: string;
  searchStartedAtMs?: number;
}

export interface IActiveGameConflictData {
  code: 'ACTIVE_GAME_EXISTS';
  message: string;
  activeGameId?: number;
  activeLobbyId?: string;
}

interface IMatchmakingErrorPayload {
  error?: string;
  message?: string;
  timestamp?: string;
  code?: string;
  activeGameId?: number;
  activeLobbyId?: string;
}

export type TRematchStateTransition =
  | 'request'
  | 'accept'
  | 'decline'
  | 'expire';

export interface IRematchStateUpdateData {
  transition: TRematchStateTransition;
  rematchRequestId: string;
  sourceLobbyId: string;
  gameId: number;
  gameName: string;
  stake: number;
  initiatorId: number;
  opponentId: number;
  acceptedUserIds: number[];
  status: ERematchRequestStatus;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  startedLobbyId?: string;
}

export interface IMatchmakingSocketReturn {
  state: TMatchmakingState;
  currentLobbyId: string | null;
  searchState: ISearchStateData | null;
  stakeOffer: IStakeOfferData | null;
  gameInfo: IGameStartedData | null;
  gameUpdate: IGameUpdateData | null;
  gameResult: IGameFinishedData | null;
  rematchStateUpdate: IRematchStateUpdateData | null;
  activeGameConflict: IActiveGameConflictData | null;
  error: string | null;
  sessionIsFreePlay: boolean;
  findGame: (gameId: number, stake: number) => Promise<void>;
  freePlay: (gameId: number, difficulty: EFreePlayDifficulty) => Promise<void>;
  acceptStakeOffer: (acceptedStake: number) => void;
  declineStakeOffer: () => void;
  confirmGame: () => void;
  declineGame: () => void;
  clientReady: () => void;
  cancelSearch: () => boolean;
  sendGameAction: (action: IGameAction) => void;
  requestGameState: () => void;
  reset: () => void;
  syncStateForModalOpen: (gameId?: number) => void;
  clearActiveGameId: () => void;
  addStateListener: (
    callback: (state: TMatchmakingState) => void,
  ) => () => void;
  addGameResultImmediateListener: (
    callback: (result: TGameResult) => void,
  ) => () => void;
  addRematchStateListener: (
    callback: (update: IRematchStateUpdateData) => void,
  ) => () => void;
}

export class MatchmakingSocketManager {
  private socket: Socket | null = null;
  private ioFactory: unknown = null; // Lazy load
  private state: TMatchmakingState = 'idle';
  private searchState: ISearchStateData | null = null;
  private stakeOffer: IStakeOfferData | null = null;
  private gameInfo: IGameStartedData | null = null;
  private gameUpdate: IGameUpdateData | null = null;
  private gameResult: IGameFinishedData | null = null;
  private rematchStateUpdate: IRematchStateUpdateData | null = null;
  private activeGameConflict: IActiveGameConflictData | null = null;
  private error: string | null = null;
  private listeners: Set<() => void> = new Set();
  private gameResultImmediateListeners: Set<(result: TGameResult) => void> =
    new Set();
  private rematchStateListeners: Set<
    (update: IRematchStateUpdateData) => void
  > = new Set();
  private currentLobbyId: string | null = null;
  private isConnecting: boolean = false;
  private loadPromise: Promise<unknown> | null = null;
  private activeGameId: string | null = null; // Tracks the current game
  private gameFinishedTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private pendingSyncAfterConnect = false;
  private pendingSyncGameId: number | undefined = undefined;
  private justSurrendered = false;
  private sessionIsFreePlay = false;
  /** Transient route hint for the current connect cycle only. Cleared on
   * game_finished, game_cancelled, and applySyncState idle. Not persisted
   * across connect attempts. */
  private routeHint: TRouteHint = null;

  private matchmakingService = new MatchmakingService();

  private async loadSocketIO() {
    if (this.ioFactory) return this.ioFactory;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = getSocketIOClient();
    this.ioFactory = await this.loadPromise;
    return this.ioFactory;
  }

  notifyListeners() {
    this.listeners.forEach((cb) => {
      cb();
    });
  }

  setState(newState: TMatchmakingState) {
    if (this.state !== newState) {
      this.state = newState;
      this.notifyListeners();
    }
  }

  getState(): TMatchmakingState {
    return this.state;
  }

  getStakeOffer(): IStakeOfferData | null {
    return this.stakeOffer;
  }

  getSearchState(): ISearchStateData | null {
    return this.searchState;
  }

  getGameInfo(): IGameStartedData | null {
    return this.gameInfo;
  }

  getGameUpdate(): IGameUpdateData | null {
    return this.gameUpdate;
  }

  getGameResult(): IGameFinishedData | null {
    return this.gameResult;
  }

  getRematchStateUpdate(): IRematchStateUpdateData | null {
    return this.rematchStateUpdate;
  }

  getActiveGameConflict(): IActiveGameConflictData | null {
    return this.activeGameConflict;
  }

  getError(): string | null {
    return this.error;
  }

  getSessionIsFreePlay(): boolean {
    return this.sessionIsFreePlay;
  }

  getCurrentLobbyId(): string | null {
    return this.currentLobbyId;
  }

  addStateListener(callback: (state: TMatchmakingState) => void): () => void {
    const wrappedCallback = () => callback(this.state);
    this.listeners.add(wrappedCallback);
    return () => {
      this.listeners.delete(wrappedCallback);
    };
  }

  addGameResultImmediateListener(
    callback: (result: TGameResult) => void,
  ): () => void {
    this.gameResultImmediateListeners.add(callback);
    return () => {
      this.gameResultImmediateListeners.delete(callback);
    };
  }

  addRematchStateListener(
    callback: (update: IRematchStateUpdateData) => void,
  ): () => void {
    this.rematchStateListeners.add(callback);
    return () => {
      this.rematchStateListeners.delete(callback);
    };
  }

  reset() {
    if (this.gameFinishedTimeoutId) {
      clearTimeout(this.gameFinishedTimeoutId);
      this.gameFinishedTimeoutId = null;
    }
    this.pendingSyncAfterConnect = false;
    this.pendingSyncGameId = undefined;
    this.justSurrendered = false;
    this.sessionIsFreePlay = false;
    this.state = 'idle';
    this.searchState = null;
    this.stakeOffer = null;
    this.gameInfo = null;
    this.gameUpdate = null;
    this.gameResult = null;
    this.rematchStateUpdate = null;
    this.activeGameConflict = null;
    this.error = null;
    this.currentLobbyId = null;
    this.activeGameId = null;
    this.routeHint = null;
    this.notifyListeners();
  }

  clearActiveGameId() {
    this.activeGameId = null;
  }

  private getRecoverableActiveLobbyId(): string | null {
    return (
      this.activeGameId ??
      this.gameUpdate?.lobbyId ??
      this.gameInfo?.lobbyId ??
      null
    );
  }

  /**
   * Checks whether an incoming event belongs to the lobby currently in the client context.
   * Guards against events from unrelated lobbies after refresh, when the server runs
   * automatic cleanup (stale/surrender) of old matches for the initiator of a new request.
   */
  private isLobbyInCurrentContext(lobbyId: string | undefined): boolean {
    if (!lobbyId) return false;
    const contextIds = [
      this.activeGameId,
      this.currentLobbyId,
      this.gameInfo?.lobbyId,
      this.gameUpdate?.lobbyId,
      this.stakeOffer?.lobbyId,
    ];
    return contextIds.some((id) => id === lobbyId);
  }

  private getCurrentLobbyIdForAction(): string | null {
    return this.currentLobbyId ?? this.getRecoverableActiveLobbyId();
  }

  private primeRecoverableActiveLobbyContext(): string | null {
    const lobbyId = this.getRecoverableActiveLobbyId();
    if (!lobbyId) {
      return null;
    }

    this.currentLobbyId = lobbyId;
    this.activeGameId = lobbyId;
    this.error = null;
    return lobbyId;
  }

  private applySyncState(data: {
    state: 'idle' | 'searching';
    lobbyId?: string;
    timeout?: number;
    searchStartedAtMs?: number;
    message?: string;
  }): void {
    if (
      this.state === 'active_game_exists' &&
      this.activeGameConflict !== null
    ) {
      return;
    }

    if (this.gameFinishedTimeoutId) {
      clearTimeout(this.gameFinishedTimeoutId);
      this.gameFinishedTimeoutId = null;
    }
    if (data.state === 'idle') {
      this.state = 'idle';
      this.searchState = null;
      this.stakeOffer = null;
      this.gameInfo = null;
      this.gameUpdate = null;
      this.gameResult = null;
      this.rematchStateUpdate = null;
      this.activeGameConflict = null;
      this.error = null;
      this.currentLobbyId = null;
      this.activeGameId = null;
      this.sessionIsFreePlay = false;
      this.routeHint = null;
    } else {
      this.state = 'searching';
      this.searchState = {
        timeout: data.timeout,
        message: data.message,
        searchStartedAtMs:
          data.searchStartedAtMs ?? this.searchState?.searchStartedAtMs,
      };
      this.currentLobbyId = data.lobbyId ?? null;
      this.stakeOffer = null;
      this.gameInfo = null;
      this.gameResult = null;
      this.rematchStateUpdate = null;
      this.activeGameConflict = null;
      this.error = null;
    }
    this.notifyListeners();
  }

  syncStateForModalOpen(gameId?: number): void {
    this.pendingSyncGameId = gameId;

    if (this.primeRecoverableActiveLobbyContext() === null) {
      this.error = null;
      this.activeGameConflict = null;
      this.setState('connecting');
    }

    if (this.socket?.connected) {
      this.socket.emit('sync', { gameId: gameId ?? undefined });
    } else {
      this.pendingSyncAfterConnect = true;
      if (!this.isConnecting) {
        void this.connect().catch((error: unknown) => {
          console.error('Failed to bootstrap matchmaking sync', error);
        });
      }
    }
  }

  private refreshRouteAffinity(context: string): void {
    void this.matchmakingService.resolveRoute().catch((error: unknown) => {
      console.error(
        `Failed to refresh matchmaking route affinity: ${context}`,
        error,
      );
    });
  }

  private blockCurrentRouteDrainStart(): boolean {
    if (!overlapStore.getState().isBannerVisible) {
      return false;
    }

    this.stakeOffer = null;
    this.searchState = null;
    this.gameInfo = null;
    this.rematchStateUpdate = null;
    this.activeGameConflict = null;
    this.error = OVERLAP_DRAIN_MATCHMAKING_BLOCK_MESSAGE;
    this.sessionIsFreePlay = false;

    if (this.state === 'stake_selection') {
      this.notifyListeners();
    } else {
      this.setState('stake_selection');
    }

    return true;
  }

  private connectionPromise: Promise<void> | null = null;

  async connect(): Promise<void> {
    if (this.socket?.connected) {
      return;
    }

    // If already connecting, wait for the existing connection
    if (this.isConnecting && this.connectionPromise) {
      return this.connectionPromise;
    }

    // Disconnect old socket if exists
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnecting = true;
    this.setState('connecting');

    this.connectionPromise = (async () => {
      try {
        // Step 1: resolve-route to determine if match affinity is active for
        // this user. Clears any stale routeHint first.
        // Errors here propagate to outer catch (fail-closed).
        this.routeHint = null;
        const routeResponse = await this.matchmakingService.resolveRoute();
        this.routeHint = extractResolvedRouteColor(routeResponse);

        // Step 2: ws-ticket (optional routeColor passed through generated dto)
        const ticketDto: WsTicketQueryDto = this.routeHint
          ? { routeColor: this.routeHint }
          : {};
        const ticketData = await wsTicketStore.getWsTicket(ticketDto);

        const ticket = (() => {
          if (isRecord(ticketData) && typeof ticketData.ticket === 'string') {
            return ticketData.ticket;
          }
          if (
            isRecord(ticketData) &&
            isRecord(ticketData.data) &&
            typeof ticketData.data.ticket === 'string'
          ) {
            return ticketData.data.ticket;
          }
          throw new Error('Invalid ticket response from wsTicketStore');
        })();

        // Dynamically load socket.io-client
        const io = (await this.loadSocketIO()) as (
          url: string,
          opts: unknown,
        ) => Socket;

        const cachedDeviceId = getCachedDeviceId();
        if (cachedDeviceId === null) {
          void ensureDeviceId().catch(() => {});
        }
        const authPayload: Record<string, unknown> = { ticket };
        if (typeof cachedDeviceId === 'string' && cachedDeviceId.length > 0) {
          authPayload.deviceId = cachedDeviceId;
        }

        // Step 3: open socket handshake with routeColor query param when present
        const wsUrl = this.routeHint
          ? `${BASE_URL}/${MATCHMAKING_WS_NAMESPACE}?routeColor=${this.routeHint}`
          : `${BASE_URL}/${MATCHMAKING_WS_NAMESPACE}`;

        this.socket = io(wsUrl, {
          auth: authPayload,
          transports: ['websocket'],
          reconnection: false,
        });

        // Set up event handlers
        this.setupEventHandlers();

        // Wait for actual connection
        await new Promise<void>((resolve, reject) => {
          if (!this.socket) {
            reject(new Error('Socket not initialized'));
            return;
          }

          const onConnect = () => {
            this.isConnecting = false;
            cleanup();
            resolve();
          };

          const onConnectError = (err: Error) => {
            this.error = err.message;
            this.setState('error');
            this.isConnecting = false;
            cleanup();
            reject(err);
          };

          const cleanup = () => {
            this.socket?.off('connect', onConnect);
            this.socket?.off('connect_error', onConnectError);
            this.connectionPromise = null;
          };

          this.socket.once('connect', onConnect);
          this.socket.once('connect_error', onConnectError);
        });
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Connection failed';
        this.setState('error');
        this.isConnecting = false;
        this.connectionPromise = null;
        throw err;
      }
    })();

    return this.connectionPromise;
  }

  private setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.isConnecting = false;
    });

    this.socket.on('authenticated', () => {
      if (this.pendingSyncAfterConnect) {
        this.pendingSyncAfterConnect = false;
        const gameId = this.pendingSyncGameId;
        this.pendingSyncGameId = undefined;
        this.socket?.emit('sync', {
          gameId: gameId ?? undefined,
        });
      }
    });

    this.socket.on('connect_error', (err: Error) => {
      this.error = err.message;
      this.activeGameConflict = null;
      this.setState('error');
      this.isConnecting = false;
      this.pendingSyncAfterConnect = false;
      this.pendingSyncGameId = undefined;
    });

    this.socket.on(
      'waiting_for_opponent',
      (data?: {
        timeout?: number;
        message?: string;
        searchStartedAtMs?: number;
      }) => {
        if (
          this.state === 'active_game_exists' &&
          this.activeGameConflict !== null
        ) {
          return;
        }

        this.searchState = {
          timeout: data?.timeout,
          message: data?.message,
          searchStartedAtMs:
            data?.searchStartedAtMs ??
            this.searchState?.searchStartedAtMs ??
            Date.now(),
        };

        if (this.state === 'searching') {
          this.notifyListeners();
          return;
        }

        this.setState('searching');
      },
    );

    this.socket.on('stake_offer', (data: IStakeOfferData) => {
      this.searchState = null;
      this.stakeOffer = data;
      this.currentLobbyId = data.lobbyId;
      this.setState('stake_offer');
    });

    this.socket.on('game_confirm', (data: IGameStartedData) => {
      if (this.activeGameId && this.activeGameId !== data.lobbyId) {
        return;
      }
      this.searchState = null;
      this.gameInfo = data;
      this.sessionIsFreePlay = data.stake === 0;
      this.currentLobbyId = data.lobbyId;
      this.stakeOffer = null;
      this.setState(
        data.currentUserConfirmed === true
          ? 'waiting_for_opponent'
          : 'game_confirm',
      );
    });

    this.socket.on('game_preparing', (data: IGameStartedData) => {
      if (this.activeGameId && this.activeGameId !== data.lobbyId) {
        return;
      }
      this.searchState = null;
      this.gameInfo = data;
      this.sessionIsFreePlay = data.stake === 0;
      this.currentLobbyId = data.lobbyId;
      this.stakeOffer = null;
      this.setState('preparing');
    });

    this.socket.on('game_started', (data: IGameStartedData) => {
      if (this.activeGameId && this.activeGameId !== data.lobbyId) {
        return;
      }
      this.searchState = null;
      this.activeGameId = data.lobbyId;
      this.gameInfo = data;
      this.sessionIsFreePlay = data.stake === 0;
      this.currentLobbyId = data.lobbyId;
      this.stakeOffer = null;
      this.setState('playing');
      this.refreshRouteAffinity('game_started');
    });

    this.socket.on(
      'game_cancelled',
      (data: { lobbyId: string; reason: string; message?: string }) => {
        if (this.activeGameId && data.lobbyId !== this.activeGameId) {
          return;
        }
        if (!this.activeGameId && !this.isLobbyInCurrentContext(data.lobbyId)) {
          return;
        }
        this.error = data.message ?? null;
        this.searchState = null;
        this.activeGameId = null;
        this.currentLobbyId = null;
        this.stakeOffer = null;
        this.gameInfo = null;
        this.gameUpdate = null;
        this.gameResult = null;
        this.rematchStateUpdate = null;
        this.activeGameConflict = null;
        this.sessionIsFreePlay = false;
        this.routeHint = null;
        this.refreshRouteAffinity('game_cancelled');
        this.setState('cancelled');
      },
    );

    this.socket.on('search_cancelled', (data?: ISearchCancelledData) => {
      if (isMatchmakingMaintenanceMessage(data?.message) && data?.message) {
        const maintenanceMessage =
          extractMaintenanceMessageFromMatchmakingMessage(data.message);

        maintenanceStore
          .getState()
          .syncStatusFromMatchmakingMessage(data.message);
        showMatchmakingMaintenanceWarningToast(maintenanceMessage);

        this.stakeOffer = null;
        this.gameInfo = null;
        this.searchState = null;
        this.currentLobbyId = null;
        this.error = null;
        this.activeGameConflict = null;
        this.sessionIsFreePlay = false;
        this.routeHint = null;

        if (!this.activeGameId) {
          this.setState('stake_selection');
        } else {
          this.notifyListeners();
        }

        return;
      }

      if (
        this.state === 'active_game_exists' &&
        this.activeGameConflict !== null
      ) {
        return;
      }

      if (this.activeGameId) {
        return;
      }
      this.stakeOffer = null;
      this.currentLobbyId = null;
      this.activeGameConflict = null;
      this.sessionIsFreePlay = false;
      this.routeHint = null;
      this.error = data?.message ?? null;
      this.searchState = null;
      this.setState('cancelled');
    });

    this.socket.on('stake_offer_declined', () => {
      if (
        this.state === 'active_game_exists' &&
        this.activeGameConflict !== null
      ) {
        return;
      }

      if (this.activeGameId) {
        return;
      }
      this.stakeOffer = null;
      this.searchState = null;
      this.setState('stake_offer_declined');
    });

    this.socket.on('game_update', (data: IGameUpdateData) => {
      if (this.activeGameId && data.lobbyId !== this.activeGameId) {
        return;
      }
      if (!this.activeGameId && !this.isLobbyInCurrentContext(data.lobbyId)) {
        return;
      }

      this.activeGameId = data.lobbyId;
      this.currentLobbyId = data.lobbyId;
      this.searchState = null;
      this.gameUpdate = data;

      if (
        this.state === 'idle' ||
        this.state === 'connecting' ||
        this.state === 'searching' ||
        this.state === 'waiting_for_opponent' ||
        this.state === 'game_confirm' ||
        this.state === 'error'
      ) {
        this.setState('playing');
        return;
      }

      this.notifyListeners();
    });

    this.socket.on('game_finished', (data: IGameFinishedData) => {
      if (this.activeGameId && data.lobbyId !== this.activeGameId) {
        return;
      }
      if (!this.activeGameId && !this.isLobbyInCurrentContext(data.lobbyId)) {
        return;
      }
      if (this.gameFinishedTimeoutId) {
        clearTimeout(this.gameFinishedTimeoutId);
        this.gameFinishedTimeoutId = null;
      }
      this.gameResult = data;
      const nextState = data.isDraw ? 'draw' : data.isWin ? 'win' : 'lose';
      this.gameResultImmediateListeners.forEach((cb) => {
        cb(nextState);
      });
      const delayMs = this.justSurrendered ? 0 : 2000;
      this.justSurrendered = false;
      this.refreshRouteAffinity('game_finished');
      this.gameFinishedTimeoutId = setTimeout(() => {
        this.gameFinishedTimeoutId = null;
        this.gameUpdate = null;
        this.activeGameId = null;
        this.routeHint = null;
        this.setState(nextState);
      }, delayMs);
    });

    this.socket.on('rematch_state_update', (data: IRematchStateUpdateData) => {
      this.rematchStateUpdate = data;
      this.rematchStateListeners.forEach((cb) => {
        cb(data);
      });
      this.notifyListeners();
    });

    this.socket.on('error', (data: IMatchmakingErrorPayload) => {
      const message = data.error || data.message || 'Unknown error';

      if (isMatchmakingMaintenanceMessage(message)) {
        const maintenanceMessage =
          extractMaintenanceMessageFromMatchmakingMessage(message);

        maintenanceStore.getState().syncStatusFromMatchmakingMessage(message);
        showMatchmakingMaintenanceWarningToast(maintenanceMessage);

        this.stakeOffer = null;
        this.gameInfo = null;
        this.searchState = null;
        this.currentLobbyId = null;
        this.error = null;
        this.activeGameConflict = null;

        if (!this.activeGameId) {
          this.setState('stake_selection');
        } else {
          this.notifyListeners();
        }

        return;
      }

      if (data.code === 'ACTIVE_GAME_EXISTS') {
        this.error = message;
        this.activeGameConflict = {
          code: 'ACTIVE_GAME_EXISTS',
          message,
          activeGameId:
            typeof data.activeGameId === 'number'
              ? data.activeGameId
              : undefined,
          activeLobbyId:
            typeof data.activeLobbyId === 'string'
              ? data.activeLobbyId
              : undefined,
        };
        this.setState('active_game_exists');
        return;
      }

      if (message === 'Action in progress') {
        const lobbyId = this.primeRecoverableActiveLobbyContext();
        if (lobbyId && this.socket?.connected) {
          this.socket.emit('get_game_state', { lobbyId });
          return;
        }
      }

      if (
        message === MATCHMAKING_INSUFFICIENT_BALANCE_ERROR &&
        !this.activeGameId
      ) {
        this.error = message;
        this.searchState = null;
        this.stakeOffer = null;
        this.gameInfo = null;
        this.gameUpdate = null;
        this.gameResult = null;
        this.rematchStateUpdate = null;
        this.currentLobbyId = null;
        this.activeGameConflict = null;
        this.sessionIsFreePlay = false;
        this.setState('stake_selection');
        return;
      }

      this.error = message;
      this.activeGameConflict = null;
      this.setState('error');
    });

    this.socket.on('disconnect', () => {
      // Do not reset state while an active game is in progress
      if (!this.activeGameId) {
        this.setState('idle');
      }
    });

    this.socket.on(
      'sync_state',
      (data: {
        state: 'idle' | 'searching' | 'preparing';
        lobbyId?: string;
        timeout?: number;
        searchStartedAtMs?: number;
        readyDeadlineAtMs?: number;
        message?: string;
      }) => {
        if (data.state === 'preparing') {
          this.state = 'preparing';
          this.searchState = null;
          this.currentLobbyId = data.lobbyId ?? null;
          this.notifyListeners();
          return;
        }
        this.applySyncState({
          state: data.state,
          lobbyId: data.lobbyId,
          timeout: data.timeout,
          searchStartedAtMs: data.searchStartedAtMs,
          message: data.message,
        });
      },
    );
  }

  findGame(gameId: number, stake: number): void {
    if (this.blockCurrentRouteDrainStart()) {
      return;
    }

    if (!this.socket?.connected) {
      this.error = 'Socket not connected';
      this.setState('error');
      return;
    }

    this.stakeOffer = null;
    this.searchState = {
      message: MATCHMAKING_SEARCHING_FOR_OPPONENT_MESSAGE,
      searchStartedAtMs: Date.now(),
    };
    this.gameInfo = null;
    this.rematchStateUpdate = null;
    this.activeGameConflict = null;
    this.error = null;
    this.sessionIsFreePlay = false;
    this.setState('searching');

    this.socket.emit('find_game', { gameId, stake });
  }

  freePlay(gameId: number, difficulty: EFreePlayDifficulty): void {
    if (this.blockCurrentRouteDrainStart()) {
      return;
    }

    if (!this.socket?.connected) {
      this.error = 'Socket not connected';
      this.setState('error');
      return;
    }

    this.stakeOffer = null;
    this.searchState = {
      message: MATCHMAKING_STARTING_FREE_PLAY_MESSAGE,
      searchStartedAtMs: Date.now(),
    };
    this.gameInfo = null;
    this.rematchStateUpdate = null;
    this.activeGameConflict = null;
    this.error = null;
    this.sessionIsFreePlay = true;
    this.setState('searching');

    this.socket.emit('free_play', { gameId, difficulty });
  }

  acceptStakeOffer(acceptedStake: number): void {
    const lobbyId = this.getCurrentLobbyIdForAction();
    if (!this.socket?.connected || !lobbyId) {
      this.error = 'No active lobby';
      this.setState('error');
      return;
    }

    this.socket.emit('accept_stake_offer', {
      lobbyId,
      acceptedStake,
    });

    this.stakeOffer = null;
    this.error = null;
    this.setState('waiting_for_opponent');
  }

  declineStakeOffer(): void {
    const lobbyId = this.getCurrentLobbyIdForAction();
    if (!this.socket?.connected || !lobbyId) {
      this.error = 'No active lobby';
      this.setState('error');
      return;
    }

    this.socket.emit('decline_stake_offer', {
      lobbyId,
    });
  }

  confirmGame(): void {
    const lobbyId = this.getCurrentLobbyIdForAction();
    if (!this.socket?.connected || !lobbyId) {
      this.error = 'No active lobby';
      this.setState('error');
      return;
    }

    this.socket.emit('confirm_game', {
      lobbyId,
    });

    this.error = null;
    this.setState('waiting_for_opponent');
  }

  declineGame(): void {
    const lobbyId = this.getCurrentLobbyIdForAction();
    if (!this.socket?.connected || !lobbyId) {
      this.error = 'No active lobby';
      this.setState('error');
      return;
    }

    this.socket.emit('decline_game', {
      lobbyId,
    });
  }

  clientReady(): void {
    const lobbyId = this.getCurrentLobbyIdForAction();
    if (!this.socket?.connected || !lobbyId) {
      return;
    }

    this.socket.emit('client_ready', {
      lobbyId,
    });
  }

  cancelSearch(): boolean {
    if (!this.socket?.connected) {
      return false;
    }

    this.socket.emit('cancel_search');
    return true;
  }

  sendGameAction(action: IGameAction): void {
    const lobbyId = this.getCurrentLobbyIdForAction();
    if (!this.socket?.connected || !lobbyId) {
      this.error = 'No active lobby';
      this.setState('error');
      return;
    }
    if (action.surrender === true) {
      this.justSurrendered = true;
    }
    this.socket.emit('game_action', {
      lobbyId,
      action,
    });
  }

  requestGameState(): void {
    const lobbyId = this.getCurrentLobbyIdForAction();
    if (!this.socket?.connected || !lobbyId) {
      return;
    }
    this.socket.emit('get_game_state', { lobbyId });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.isConnecting = false;
  }
}

const manager = new MatchmakingSocketManager();

type TE2EWindow = globalThis.Window & {
  __E2E_TEST__?: boolean;
  __MATCHMAKING_MANAGER__?: MatchmakingSocketManager;
};

function isE2ETestWindow(w: unknown): w is TE2EWindow {
  return typeof w === 'object' && w !== null && '__E2E_TEST__' in w;
}

if (typeof window !== 'undefined') {
  const w: unknown = window;
  if (isE2ETestWindow(w) && w.__E2E_TEST__) {
    Object.assign(window, { __MATCHMAKING_MANAGER__: manager });
  }
}

export type { IGameUpdateData, IGameFinishedData };

export const useMatchmakingSocket = (): IMatchmakingSocketReturn => {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const unsubscribe = manager.addStateListener(() => {
      forceUpdate({});
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const findGame = useCallback(async (gameId: number, stake: number) => {
    try {
      await manager.connect();
      manager.findGame(gameId, stake);
    } catch {
      // Error handled in manager
    }
  }, []);

  const freePlay = useCallback(
    async (gameId: number, difficulty: EFreePlayDifficulty) => {
      try {
        await manager.connect();
        manager.freePlay(gameId, difficulty);
      } catch {
        // Error handled in manager
      }
    },
    [],
  );

  const acceptStakeOffer = useCallback((acceptedStake: number) => {
    manager.acceptStakeOffer(acceptedStake);
  }, []);

  const declineStakeOffer = useCallback(() => {
    manager.declineStakeOffer();
  }, []);

  const cancelSearch = useCallback((): boolean => {
    return manager.cancelSearch();
  }, []);

  const confirmGame = useCallback(() => {
    manager.confirmGame();
  }, []);

  const declineGame = useCallback(() => {
    manager.declineGame();
  }, []);

  const clientReady = useCallback(() => {
    manager.clientReady();
  }, []);

  const sendGameAction = useCallback((action: IGameAction) => {
    manager.sendGameAction(action);
  }, []);

  const requestGameState = useCallback(() => {
    manager.requestGameState();
  }, []);

  const reset = useCallback(() => {
    manager.reset();
  }, []);

  const syncStateForModalOpen = useCallback((gameId?: number) => {
    manager.syncStateForModalOpen(gameId);
  }, []);

  const addStateListener = useCallback(
    (callback: (state: TMatchmakingState) => void) => {
      return manager.addStateListener(callback);
    },
    [],
  );

  const addGameResultImmediateListener = useCallback(
    (callback: (result: TGameResult) => void) => {
      return manager.addGameResultImmediateListener(callback);
    },
    [],
  );

  const addRematchStateListener = useCallback(
    (callback: (update: IRematchStateUpdateData) => void) => {
      return manager.addRematchStateListener(callback);
    },
    [],
  );

  return {
    state: manager.getState(),
    currentLobbyId: manager.getCurrentLobbyId(),
    searchState: manager.getSearchState(),
    stakeOffer: manager.getStakeOffer(),
    gameInfo: manager.getGameInfo(),
    gameUpdate: manager.getGameUpdate(),
    gameResult: manager.getGameResult(),
    rematchStateUpdate: manager.getRematchStateUpdate(),
    activeGameConflict: manager.getActiveGameConflict(),
    error: manager.getError(),
    sessionIsFreePlay: manager.getSessionIsFreePlay(),
    findGame,
    freePlay,
    acceptStakeOffer,
    declineStakeOffer,
    confirmGame,
    declineGame,
    clientReady,
    cancelSearch,
    sendGameAction,
    requestGameState,
    reset,
    syncStateForModalOpen,
    clearActiveGameId: manager.clearActiveGameId.bind(manager),
    addStateListener,
    addGameResultImmediateListener,
    addRematchStateListener,
  };
};
